import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import EnterpriseIcon, { PageFirstIcon, ChevronLeftIcon, ChevronRightIcon, PageLastIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React__default, { useState, useEffect } from 'react';
import alertUserOfDeprecatedProps from '../utils/deprecation.js';
import useCanvasId from '../utils/useCanvasId.js';
import Button from './Button.js';
import Input from './Input/index.js';

var _excluded = ["className", "currentPage", "data-testid", "onRequestNext", "onRequestPage", "onRequestPrev", "onRequestFirst", "onRequestLast", "pageSelector", "totalPages"];
var fillPages = function fillPages(startPage, endPage) {
  return Array(endPage).fill(startPage).map(function (x, y) {
    var value = x + y;
    return {
      label: value,
      value: value
    };
  });
};
var PageSelector = function PageSelector(_ref) {
  var currentPage = _ref.currentPage,
    dataTestId = _ref['data-testid'],
    onRequestPage = _ref.onRequestPage,
    pageSelector = _ref.pageSelector,
    totalPages = _ref.totalPages;
  var _useState = useState(currentPage),
    _useState2 = _slicedToArray(_useState, 2),
    currentPageInputValue = _useState2[0],
    setCurrentPageInputValue = _useState2[1];
  useEffect(function () {
    setCurrentPageInputValue(currentPage);
  }, [currentPage]);
  var showPageSelector = pageSelector && currentPage && totalPages;
  var pageSelectId = "".concat(useCanvasId(), "-pageSelect");
  if (!currentPage || !totalPages) {
    console.error('Hey Canvas User! The pagination page selector requires that currentPage and totalPages are set');
  }
  if (!showPageSelector) {
    return null;
  }
  var pageRange = fillPages(1, totalPages);
  return /*#__PURE__*/React__default.createElement("div", {
    className: "C-Pagination__pageSelector hc-ph-normal"
  }, /*#__PURE__*/React__default.createElement(Input.Select, {
    defaultValue: 1,
    id: pageSelectId,
    value: currentPageInputValue,
    onChange: function onChange(event) {
      var selectedPage = pageRange[event.target.value].value;
      setCurrentPageInputValue(selectedPage);
      if (selectedPage) {
        onRequestPage(event, selectedPage);
      }
    },
    options: pageRange,
    "data-testid": dataTestId ? "".concat(dataTestId, "_pageSelector") : null
  }));
};
var Pagination = function Pagination(props) {
  var className = props.className,
    currentPage = props.currentPage,
    dataTestId = props['data-testid'],
    onRequestNext = props.onRequestNext,
    onRequestPage = props.onRequestPage,
    onRequestPrev = props.onRequestPrev,
    onRequestFirst = props.onRequestFirst,
    onRequestLast = props.onRequestLast,
    pageSelector = props.pageSelector,
    totalPages = props.totalPages,
    otherProps = _objectWithoutProperties(props, _excluded);
  alertUserOfDeprecatedProps('Pagination', otherProps);
  var finalClasses = classnames('C-Pagination', {
    '--pageIndicator': totalPages && currentPage
  }, className);
  var showPageSelector = pageSelector === true;
  return /*#__PURE__*/React__default.createElement("div", {
    className: finalClasses,
    "data-testid": dataTestId,
    role: "navigation",
    "aria-label": "pagination"
  }, onRequestFirst !== undefined && /*#__PURE__*/React__default.createElement(Button, {
    type: "ghost",
    iconOnly: true,
    onClick: onRequestFirst,
    disabled: !onRequestFirst || currentPage === 1,
    "data-testid": dataTestId ? "".concat(dataTestId, "_first") : null,
    "aria-label": "go to first page"
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    size: "lg",
    icon: PageFirstIcon
  })), /*#__PURE__*/React__default.createElement(Button, {
    type: "ghost",
    className: "C-Pagination__prev",
    iconOnly: true,
    onClick: onRequestPrev,
    disabled: !onRequestPrev || currentPage === 1,
    "data-testid": dataTestId ? "".concat(dataTestId, "_back") : null,
    "aria-label": "go to previous page"
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    size: "lg",
    icon: ChevronLeftIcon
  })), totalPages && currentPage && /*#__PURE__*/React__default.createElement("div", {
    "aria-live": "polite",
    className: "C-Pagination__pageIndicator hc-mb-none",
    "data-testid": dataTestId ? "".concat(dataTestId, "_pageIndicator") : null
  }, "Page", ' ', showPageSelector && /*#__PURE__*/React__default.createElement(PageSelector, {
    currentPage: currentPage,
    pageSelector: pageSelector,
    totalPages: totalPages,
    onRequestPage: onRequestPage,
    "data-testid": dataTestId ? "".concat(dataTestId) : null
  }), !showPageSelector && currentPage, " of ", totalPages), /*#__PURE__*/React__default.createElement(Button, {
    type: "ghost",
    className: "C-Pagination__next",
    iconOnly: true,
    onClick: onRequestNext,
    disabled: !onRequestNext || currentPage === totalPages,
    "data-testid": dataTestId ? "".concat(dataTestId, "_next") : null,
    "aria-label": "go to next page"
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    size: "lg",
    icon: ChevronRightIcon
  })), onRequestLast !== undefined && /*#__PURE__*/React__default.createElement(Button, {
    type: "ghost",
    iconOnly: true,
    onClick: onRequestLast,
    disabled: !onRequestLast || currentPage === totalPages,
    "data-testid": dataTestId ? "".concat(dataTestId, "_last") : null,
    "aria-label": "go to last page"
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    size: "lg",
    icon: PageLastIcon
  })));
};
Pagination.displayName = 'Pagination';

export { Pagination as default, fillPages };
